import { useState } from 'react';
import {
  CloseButton,
  Dialog,
  Portal,
  Button,
  Field,
  Flex,
  Grid,
  GridItem,
  Input,
  Text,
  Box,
  Pagination,
  ButtonGroup,
  IconButton,
} from '@chakra-ui/react';
import { LuSearch } from 'react-icons/lu';
import CpfInput from '@components/CpfInput';
import PhoneInput from '@components/PhoneInput';
import { onlyDigits } from '@utils/formats';
import api from '@services/api';
import { PHONE_NUMBER, REGEX_FORMATTED_TAX_ID, REGEX_EMAIL } from '@utils/regex';
import { toast } from 'react-toastify';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { datadogRum } from '@datadog/browser-rum';
import Loading from '@components/Loading';
import CustomDialogBackdrop from '@components/CustomDialogBackdrop';

import NewExecutionCard from './NewExecutionCard';
import type { WalkInCustomerType } from '../types';

interface Props {
  handleClose: () => void;
};

const NewExecutionDialog = ({ handleClose }: Props) => {
  const [loading, setLoading] = useState(false);
  const [userName, setUserName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [taxId, setTaxId] = useState('');
  const [customersList, setCustomersList] = useState<WalkInCustomerType[]>([]);
  const [page, setPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  const onSearch = async (newPage?:number) => {
    if (!userName && !phoneNumber && !email && !taxId) {
      toast.error('Preencha ao menos um campo para realizar a pesquisa.');
      return;
    }

    if (phoneNumber && !PHONE_NUMBER.test(phoneNumber)) {
      toast.error('Número de telefone inválido.');
      return;
    }

    if (taxId && !REGEX_FORMATTED_TAX_ID.test(taxId)) {
      toast.error('CPF inválido. Certifique-se de que está no formato correto: 000.000.000-00');
      return;
    }

    if (email && !REGEX_EMAIL.test(email)) {
      toast.error('E-mail inválido');
      return;
    }

    setLoading(true);

    if (newPage) {
      setPage(newPage);
    }

    const params = {
      pageIndex: newPage || page,
      pageLimit: 5,
      name: userName,
      email: email,
      taxId: onlyDigits(taxId),
      phoneNumber: onlyDigits(phoneNumber),
    };

    try {
      const response = await api.get('/walk_in/search', {
        params,
      });

      setCustomersList(response.data.data);
      setTotalResults(response.data.total);

      if (response.data.data.length === 0) {
        toast.info('Nenhum cliente encontrado com os critérios informados.');
      }
    } catch (error) {
      toast.error('Erro ao carregar pesquisar o cliente. Tente novamente!');
      datadogRum.addError(new Error('Fail to search walk-in customers'), {
        error,
        params,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey && e.key === 'Enter') {
      e.preventDefault();
      onSearch();
    }
  };

  return (
    <Dialog.Root
      open
      size="xl"
      placement="center"
      onOpenChange={handleClose}
      closeOnInteractOutside={false}
    >
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>
                Novo atendimento
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <Text fontSize="sm" mb={4} color="fg.muted">
                Pesquise um cliente para iniciar um novo atendimento.
              </Text>

              <Grid templateColumns="repeat(4, 1fr)" gap="2">
                <GridItem colSpan={{ base: 4, md: 2, lg: 1 }}>
                  <Field.Root>
                    <Field.Label>Nome</Field.Label>
                    <Input
                      name="name"
                      placeholder="Pesquisar por nome"
                      variant="subtle"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </Field.Root>
                </GridItem>
                <GridItem colSpan={{ base: 4, md: 2, lg: 1 }}>
                  <Text fontSize="sm" mb={1} fontWeight={500}>
                    Telefone
                  </Text>
                  <PhoneInput
                    name="phoneNumber"
                    placeholder="Pesquisar pelo telefone"
                    variant="subtle"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </GridItem>
                <GridItem colSpan={{ base: 4, md: 2, lg: 1 }}>
                  <Field.Root>
                    <Field.Label>E-mail</Field.Label>
                    <Input
                      name="email"
                      placeholder="Pesquisar pelo e-mail"
                      variant="subtle"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </Field.Root>
                </GridItem>
                <GridItem colSpan={{ base: 4, md: 2, lg: 1 }}>
                  <Field.Root>
                    <Field.Label>CPF</Field.Label>
                    <CpfInput
                      name="taxId"
                      placeholder="Pesquisar pelo CPF"
                      variant="subtle"
                      value={taxId}
                      onChange={(e) => setTaxId(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </Field.Root>
                </GridItem>
              </Grid>
              <Flex justifyContent="center" mt={4} gap={1}>
                <Button size="sm" onClick={() => onSearch()}>
                  <LuSearch /> Pesquisar
                </Button>
              </Flex>

              {loading && (
                <Box mt={6}>
                  <Loading />
                </Box>
              )}

              {customersList.length > 0 && (
                <Box mt={6}>
                  {!loading && (
                    <>
                      <hr />
                      {customersList.map((customer) => (
                        <NewExecutionCard
                          key={customer.id}
                          customer={customer}
                        />
                      ))}
                    </>
                  )}

                  <Flex justifyContent="flex-end" mt={4}>
                    <Pagination.Root
                      count={totalResults}
                      pageSize={5}
                      page={page}
                      onPageChange={(e) => onSearch(e.page)}
                    >
                      <ButtonGroup variant="ghost" size={{ base: 'xs', md: 'sm' }} gap={0}>
                        <Pagination.PrevTrigger asChild>
                          <IconButton>
                            <HiChevronLeft />
                          </IconButton>
                        </Pagination.PrevTrigger>

                        <Pagination.Items
                          render={(page) => (
                            <IconButton variant={{ base: "ghost", _selected: "outline" }}>
                              {page.value}
                            </IconButton>
                          )}
                        />

                        <Pagination.NextTrigger asChild>
                          <IconButton>
                            <HiChevronRight />
                          </IconButton>
                        </Pagination.NextTrigger>
                      </ButtonGroup>
                    </Pagination.Root>
                  </Flex>
                </Box>
              )}
            </Dialog.Body>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default NewExecutionDialog;
